import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  Button,
  HelpBlock,
  InputText,
  SuccessMessage,
} from '../../common/form';
import {
  createNavigationItem,
  updateNavigationItem,
  publishNavigationItem,
  unpublishNavigationItem,
  deleteNavigationItem,
} from '../../../../services/lwadmin/navigation/navigation-item-service';
import useNavigationStore from '../state-hook';
import { navigationSlotTemplateForOrder } from '../navigation-slot-defaults';

const SSuccessMessage = styled(SuccessMessage)`
  margin-bottom: 0;
`;

const Form = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  padding: 12px 0;

  & > .name {
    width: 30%;
  }

  & > .url {
    width: 40%;
  }

  & > .action {
    width: 30%;
  }
`;

const SInputText = styled(InputText)`
  width: 90%;
`;

const SButton = styled(Button)`
  margin-top: 0;
  margin-right: 10px;
  padding: 0 10px;
`;

const SAVE_SUCCESS_MESSAGE = 'Navigation item successfully saved';
const REMOVE_SUCCESS_MESSAGE = 'Navigation item removed';

export default function Item(props) {
  const set = useNavigationStore(state => state.set);
  const [saving, setSaving] = React.useState(false);
  const [successMessage, setSuccessMessage] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [item, setItem] = React.useState(_.get(props, 'item', {}));
  const {
    id,
    name,
    url,
    order,
    published,
  } = item;

  const onAttributeChange = e => {
    const attrValue = _.get(e, 'target.value', '');
    const attrName = _.get(e, 'target.name');
    const newItem = { ...item };
    newItem[attrName] = attrValue;
    setItem(newItem);

    set(state => {
      state.items[order] = newItem;
    });
  };

  const saveItem = () => {
    setSaving(true);

    const payload = { name, url, order };

    const createOrUpdate = id ? updateNavigationItem(id, payload) : createNavigationItem(payload);

    createOrUpdate.then(resp => {
      setSaving(false);
      setError(null);
      setSuccessMessage(SAVE_SUCCESS_MESSAGE);
      setTimeout(() => setSuccessMessage(null), 3000);

      const createdId = _.get(resp, 'data.id');
      if (createdId) {
        setItem({ ...item, id: createdId });
      }
    }).catch(errorResp => {
      setSaving(false);
      setSuccessMessage(null);
      setError(_.get(errorResp, 'response.data'));
    });
  };

  const publishOrUnpublish = () => {
    const apiToCall = published ? unpublishNavigationItem : publishNavigationItem;

    apiToCall(id).then(() => setItem({ ...item, published: !published }));
  };

  const removeItem = () => {
    if (!window.confirm('Remove this navigation item? This slot will revert to its default until you save a new link.')) {
      return;
    }
    setSaving(true);
    deleteNavigationItem(id).then(() => {
      setSaving(false);
      setError(null);
      const cleared = navigationSlotTemplateForOrder(order);
      setItem(cleared);
      set(state => {
        state.items[order] = cleared;
      });
      setSuccessMessage(REMOVE_SUCCESS_MESSAGE);
      setTimeout(() => setSuccessMessage(null), 3000);
    }).catch(errorResp => {
      setSaving(false);
      setSuccessMessage(null);
      setError(_.get(errorResp, 'response.data'));
    });
  };

  return (
    <>
      {successMessage && <SSuccessMessage>{successMessage}</SSuccessMessage>}
      {error && <HelpBlock>{_.compact([error.name, error.url]).join(' and ')}</HelpBlock>}
      <Form>
        <span className="name">
          <SInputText
            type="text"
            name="name"
            value={name}
            placeholder="Name"
            onChange={onAttributeChange}
          />
        </span>
        <span className="url">
          <SInputText
            type="text"
            name="url"
            value={url}
            placeholder="URL"
            onChange={onAttributeChange}
          />
        </span>
        <span className="action">
          <SButton className="btn btn-default" onClick={saveItem} disabled={saving}>
            {saving ? 'Saving' : 'Save'}
          </SButton>
          {id && (
            <>
              <SButton className="btn btn-default" onClick={publishOrUnpublish} disabled={saving}>
                {published ? 'Unpublish' : 'Publish'}
              </SButton>
              <SButton className="btn btn-danger" onClick={removeItem} disabled={saving}>
                Remove
              </SButton>
            </>
          )}
        </span>
      </Form>
    </>
  );
}

Item.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    url: PropTypes.string,
    order: PropTypes.number,
    published: PropTypes.bool,
  }).isRequired,
};
